import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Space, Badge, Button, Drawer, List, Tag, Empty, Switch, Spin } from 'antd';
import {
  ShopOutlined,
  UserOutlined,
  LogoutOutlined,
  KeyOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getSelectedKeys,
  getOpenKeys,
  menuIconMap,
  livePetSubMenuItems,
  LIVE_PET_MENU_ROOT_KEY,
  LIVE_PET_INCOMING_MENU_KEY,
  type MenuItem,
} from '../utils/menuConfig';
import ChangePasswordModal from './ChangePasswordModal';
import StoreSwitchModal from './StoreSwitchModal';
import { StoreNotificationProvider, useStoreNotifications } from '../contexts/StoreNotificationContext';
import type { StoreMenu, StoreWebNotification } from '../types';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const PageFallback = (
  <div style={{ minHeight: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Spin />
  </div>
);

const getMenuBadgeCount = (menuKey: string, pendingByType: any) => {
  if (menuKey === '/business') {
    return (pendingByType.wash || 0) + (pendingByType.boarding || 0) + (pendingByType.mall_pickup || 0);
  }
  if (menuKey === '/business/wash-orders') return pendingByType.wash || 0;
  if (menuKey === '/business/boarding-orders') return pendingByType.boarding || 0;
  if (menuKey === '/business/mall-orders') return pendingByType.mall_pickup || 0;
  if (menuKey === LIVE_PET_MENU_ROOT_KEY || menuKey === LIVE_PET_INCOMING_MENU_KEY) {
    return pendingByType.live_pet_transfer || 0;
  }
  return 0;
};

const MenuBadgeLabel: React.FC<{ menuKey: string; label: React.ReactNode }> = ({ menuKey, label }) => {
  const { pendingByType, unfinishedByType, pendingPaymentByType } = useStoreNotifications();
  const count = getMenuBadgeCount(menuKey, { ...pendingByType, ...unfinishedByType });
  const navigate = useNavigate();
  const orderType = menuKey === '/business/wash-orders' ? 'wash' : menuKey === '/business/boarding-orders' ? 'boarding' : null;
  if (!count) return <>{label}</>;
  return (
    <Badge
      count={count}
      title={orderType ? `未结束 ${count} 单，待支付 ${pendingPaymentByType[orderType]} 单` : undefined}
      onClick={orderType ? (event) => { event.stopPropagation(); navigate(`${menuKey}?status_group=unfinished`); } : undefined}
      offset={[14, -3]}
      style={{
        minWidth: 18,
        height: 18,
        lineHeight: '18px',
        padding: '0 5px',
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <span>{label}</span>
    </Badge>
  );
};

const withNotificationBadgeLabels = (items: MenuItem[]): MenuItem[] => {
  return items.map((item: any) => {
    if (!item) return item;
    const key = String(item.key || '');
    const children = item.children ? withNotificationBadgeLabels(item.children) : undefined;
    if (
      !key.startsWith('/business')
      && key !== LIVE_PET_MENU_ROOT_KEY
      && key !== LIVE_PET_INCOMING_MENU_KEY
    ) {
      return children ? { ...item, children } : item;
    }
    return {
      ...item,
      label: <MenuBadgeLabel menuKey={key} label={item.label} />,
      children,
    };
  });
};

const buildMenuItemsFromRbac = (menus: StoreMenu[] = []): MenuItem[] => {
  return menus.map((menu) => {
    if (menu.menu_code === 'service.live_animal') {
      return {
        key: LIVE_PET_MENU_ROOT_KEY,
        icon: menu.icon ? menuIconMap[menu.icon] : undefined,
        label: menu.menu_name,
        children: livePetSubMenuItems,
      } as MenuItem;
    }
    const children = buildMenuItemsFromRbac(menu.children || []);
    return {
      key: menu.route_path || `/${menu.menu_code.replace(/\./g, '/')}`,
      icon: menu.icon ? menuIconMap[menu.icon] : undefined,
      label: menu.menu_name,
      children: children.length ? children : undefined,
    } as MenuItem;
  });
};

const collectMenuRoutePaths = (menus: StoreMenu[] = [], result: string[] = []) => {
  menus.forEach((menu) => {
    if (menu.route_path) {
      result.push(menu.route_path);
    }
    collectMenuRoutePaths(menu.children || [], result);
  });
  return result;
};

const getDefaultRoutePath = (menus: StoreMenu[] = [], routes: string[] = []) => {
  if (routes.includes('/dashboard') || collectMenuRoutePaths(menus).includes('/dashboard')) {
    return '/dashboard';
  }
  const routePath = routes.find((route) => route.startsWith('/'));
  if (routePath) {
    return routePath;
  }
  return collectMenuRoutePaths(menus)[0] || '/';
};

const StoreLayoutContent: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    pendingCount,
    pendingList,
    pendingListLoading,
    voiceEnabled,
    setVoiceEnabled,
    loadPendingList,
  } = useStoreNotifications();
  const [collapsed, setCollapsed] = useState(false);
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [storeSwitchOpen, setStoreSwitchOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>(getOpenKeys(location.pathname));

  const selectedKeys = useMemo(
    () => getSelectedKeys(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const baseMenuItems = useMemo(() => {
    return buildMenuItemsFromRbac(user?.menus || []);
  }, [user?.menus]);
  const menuItemsWithBadges = useMemo(() => withNotificationBadgeLabels(baseMenuItems), [baseMenuItems]);

  useEffect(() => {
    const nextOpenKeys = getOpenKeys(location.pathname);
    setOpenKeys((current) => {
      if (current.length === nextOpenKeys.length && current.every((key, index) => key === nextOpenKeys[index])) {
        return current;
      }
      return nextOpenKeys;
    });
  }, [location.pathname]);

  // 顶级菜单 key 列表（用于手风琴模式判断）
  const rootMenuKeys = useMemo(() => (
    baseMenuItems
      .filter((item): item is { key: string; children?: unknown[] } => !!(item && 'children' in item && (item as { children?: unknown[] }).children))
      .map(item => item.key as string)
  ), [baseMenuItems]);

  const handleOpenChange = useCallback((keys: string[]) => {
    const latestOpen = keys.find(k => !openKeys.includes(k));
    if (latestOpen && rootMenuKeys.includes(latestOpen)) {
      // 只保留最新展开的顶级菜单
      setOpenKeys([latestOpen]);
    } else {
      setOpenKeys(keys);
    }
  }, [openKeys, rootMenuKeys]);

  const openNotificationTarget = (item: StoreWebNotification) => {
    const targetUrl = item.payload?.target_url
      || (item.order_type === 'live_pet_transfer'
        ? '/service/live-animal?tab=incoming'
        : (item.order_type === 'boarding' ? '/business/boarding-orders?status=0' : '/business/wash-orders?status=0'));
    setMessageOpen(false);
    navigate(targetUrl);
  };

  const userMenuItems = [
    {
      key: 'switch-store',
      icon: <SwapOutlined />,
      label: '切换门店',
      onClick: () => setStoreSwitchOpen(true),
    },
    {
      key: 'change-password',
      icon: <KeyOutlined />,
      label: '修改密码',
      onClick: () => setPwdModalOpen(true),
    },
    { type: 'divider' as const },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
      onClick: logout,
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 左侧菜单 */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="light"
        width={220}
        style={{
          borderRight: '1px solid #f0f0f0',
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10,
        }}
        trigger={null}
      >
        {/* Logo 区域 */}
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? 0 : '0 20px',
            borderBottom: '1px solid #f0f0f0',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onClick={() => navigate(getDefaultRoutePath(user?.menus || [], user?.routes || []))}
        >
          <ShopOutlined style={{ fontSize: 24, color: '#667eea' }} />
          {!collapsed && (
            <span
              style={{
                fontSize: 16,
                fontWeight: 600,
                marginLeft: 10,
                color: '#1f1f1f',
                whiteSpace: 'nowrap',
              }}
            >
              门店管理
            </span>
          )}
        </div>

          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
            items={menuItemsWithBadges}
            onClick={({ key }) => navigate(key)}
            style={{ border: 'none', paddingTop: 8 }}
          />
      </Sider>

      {/* 右侧内容区 */}
      <Layout style={{ marginLeft: collapsed ? 80 : 220, transition: 'all 0.2s' }}>
        {/* 顶部导航 */}
        <Header
          style={{
            background: '#fff',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            position: 'sticky',
            top: 0,
            zIndex: 9,
            height: 64,
          }}
        >
          {/* 左侧：折叠按钮 + 门店头像 + 门店名称 */}
          <Space size={16} align="center">
            <span
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 18, cursor: 'pointer', color: '#666' }}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            <Space size={10} align="center">
              <Avatar
                size={36}
                icon={<ShopOutlined />}
                style={{ backgroundColor: '#667eea' }}
              />
              <Text strong style={{ fontSize: 15 }}>
                {user?.store_name || '门店'}
              </Text>
            </Space>
          </Space>

          {/* 右侧：消息通知 + 用户信息 + 下拉菜单 */}
          <Space size={18} align="center">
            <Badge count={pendingCount} size="small">
              <Button
                type="text"
                shape="circle"
                icon={<BellOutlined style={{ fontSize: 18 }} />}
                onClick={() => {
                  setMessageOpen(true);
                  loadPendingList();
                }}
              />
            </Badge>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }} size={8}>
                <Avatar size={32} icon={<UserOutlined />} />
                <div style={{ lineHeight: 1.4 }}>
                  <Text strong style={{ display: 'block', fontSize: 13 }}>
                    {user?.emp_name}
                  </Text>
                  <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                    {user?.mobile || ''}
                  </Text>
                </div>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* 页面内容 */}
        <Content
          style={{
            margin: 16,
            padding: 20,
            background: '#f5f5f5',
            borderRadius: 8,
            minHeight: 'calc(100vh - 96px)',
          }}
        >
          <Suspense fallback={PageFallback}>
            <Outlet />
          </Suspense>
        </Content>
      </Layout>

      {/* 修改密码弹窗 */}
      <StoreSwitchModal open={storeSwitchOpen} onClose={() => setStoreSwitchOpen(false)} />
      <ChangePasswordModal open={pwdModalOpen} onClose={() => setPwdModalOpen(false)} />

      <Drawer
        title="消息推送"
        open={messageOpen}
        onClose={() => setMessageOpen(false)}
        width={420}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 0 16px',
            marginBottom: 12,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div>
            <div style={{ fontWeight: 500 }}>语音提示</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              仅当前浏览器生效
            </Text>
          </div>
          <Switch
            checked={voiceEnabled}
            checkedChildren="开"
            unCheckedChildren="关"
            onChange={(checked) => { setVoiceEnabled(checked); }}
          />
        </div>
        {!pendingListLoading && pendingList.length === 0 ? (
          <Empty description="暂无待处理事项" />
        ) : (
          <List
            loading={pendingListLoading}
            dataSource={pendingList}
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button key="handle" type="link" onClick={() => openNotificationTarget(item)}>
                    去处理
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={(
                    <Space>
                      <span>{item.title}</span>
                      <Tag color={item.order_type === 'live_pet_transfer' ? 'purple' : (item.order_type === 'boarding' ? 'orange' : 'blue')}>
                        {item.order_type === 'live_pet_transfer' ? '活体迁移' : (item.order_type === 'boarding' ? '寄养' : '洗护')}
                      </Tag>
                    </Space>
                  )}
                  description={(
                    <div>
                      <div>{item.content}</div>
                      <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                        {item.order_code}
                        {item.payload?.member_phone ? ` · ${item.payload.member_phone}` : ''}
                        {item.order_type === 'live_pet_transfer'
                          ? ' · 待接收确认'
                          : ` · 已提醒 ${item.remind_count}/6 次`}
                      </div>
                    </div>
                  )}
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </Layout>
  );
};

const StoreLayout: React.FC = () => (
  <StoreNotificationProvider>
    <StoreLayoutContent />
  </StoreNotificationProvider>
);

export default StoreLayout;
